import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './output.css';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const Header = ({ default_project }) => {
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [userName, setUserName] = useState('');
  const [isProjectSelected, setIsProjectSelected] = useState(false);

  const authContext = useAuth();
  const { logout } = authContext || {}; // Fallback if context is not available

  const navigate = useNavigate();

  // Get authentication values from localStorage
  const user_id = localStorage.getItem('user_id');
  const corporate_account = localStorage.getItem('corporate_account');
  const account_description = localStorage.getItem('account_description');
  const login_token = localStorage.getItem('authToken');
  const current_project = localStorage.getItem('current_project');

  // AUTHENTICATION CHECK - This will apply to ALL pages that use Header
  useEffect(() => {
    if (!user_id || !corporate_account || !account_description || !login_token) {
      console.log('Missing authentication data, redirecting to login...');
      navigate('/LoginAndAuthentication');
      return;
    }
  }, [user_id, corporate_account, account_description, login_token, navigate]);

  // Check if project is selected
  useEffect(() => {
    const checkProjectSelection = () => {
      const currentProject = localStorage.getItem('current_project');
      setIsProjectSelected(currentProject && currentProject !== 'null');
    };

    // Initial check
    checkProjectSelection();

    // Listen for storage changes (when project is selected)
    const handleStorageChange = () => {
      checkProjectSelection();
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check periodically in case localStorage is updated in same tab
    const interval = setInterval(checkProjectSelection, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Fetch user name
  useEffect(() => {
    const fetchUserName = async () => {
      if (current_project && user_id) {
        try {
          const userAccessResponse = await fetch(appConfig.getUrl('/get_user_info'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${login_token}`
            },
            body: JSON.stringify({
              user_id,
              project_id: current_project
            })
          });

          const userAccessData = await userAccessResponse.json();

          if (userAccessData.status === 'Success' && userAccessData.user_name) {
            setUserName(userAccessData.user_name);
          }
        } catch (error) {
          console.error('Error fetching user info:', error);
        }
      }
    };

    fetchUserName();
  }, [current_project, user_id, login_token]);

  const handleMenuClick = (idx) => {
    if (!isProjectSelected) return;
    setOpenMenuId(openMenuId === idx ? null : idx);
  };

  // Add click outside handler
  useEffect(() => {
    const closeMenu = (e) => {
      if (!e.target.closest('.menu-container')) {
        setOpenMenuId(null);
      }
      if (!e.target.closest('.account-menu-container')) {
        setShowAccountMenu(false);
      }
      if (!e.target.closest('.nav-menu-container')) {
        setShowNavMenu(false);
      }
    };
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const handleLogout = async () => {
    try {
      // Clear all authentication data from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user_id');
      localStorage.removeItem('user_name');
      localStorage.removeItem('user_since');
      localStorage.removeItem('corporate_account');
      localStorage.removeItem('default_project');
      localStorage.removeItem('account_description');
      localStorage.removeItem('current_project');

      // Call the logout function from AuthContext if it exists
      if (logout) {
        logout();
      }

      // Close the menu
      setShowAccountMenu(false);

      // Navigate to login page
      navigate('/LoginAndAuthentication');

      // Optional: Force a page reload to ensure clean state
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, still navigate to login
      navigate('/LoginAndAuthentication');
    }
  };

  const handleBackToWebsite = () => {
    // Navigate to the homepage
    navigate('/');
    setShowNavMenu(false);
  };

  // Define menu sections based on RequirementsPortal - Updated to reflect moved items
  const menuSections = [
    {
      title: "My Dashboard",
      items: [
        { text: "My Requirement Approvals", path: "/MyRequirementApprovals" },
        { text: "My RAID Assignments", path: "/RaidLogManagement?mode=myassignments" }
      ]
    },


    {
      title: "Reports",
      items: [
                { text: "Status vs Priority Report", path: "/StatusPriorityReport" },
                { text: "Requirements Approvals Report", path: "/RequirementApprovalDashboard" },
                { text: "Product Evaluation Report", path: "/ProductClassificationReport" }
             ]
    },


    {
      title: "Requirements Management",
      items: [
        { text: "Functional Requirements", path: "/BaseRequirementsManagement" },
        { text: "Integration Requirements", path: "/IntegrationRequirementsManagement" }
      ]
    },
    {
      title: "Project Management",
      items: [
        { text: "Manage RAID Log", path: "/RaidLogManagement?mode=allassignees" }
      ]
    },
    {
      title: "Admin Functions",
      items: [
        { text: "Manage Projects", path: "/ManageProjects" },
        { text: "Manage Functional Hierarchy", path: "/FunctionalLevels" },
        { text: "Manage Project Team Definitions", path: "/BusinessTeams" },
        { text: "Manage Users", path: "/ManageUsers" },
        { text: "Manage Access Level Definitions", path: "/ManageAccessLevels" },
        { text: "Manage Status Codes", path: "/ManageStatusCodes" },
        { text: "Manage Exceptions Configuration", path: "/FunctionalAttributes" },
        { text: "Manage Provider & Consumer Systems", path: "/IntegrationSystems" },
        { text: "Manage Products & User Access", path: "/ManageProducts" },
        { text: "Manage Functional Domains", path: "/FunctionalDomains" }
      ]
    }
  ];

  // Show loading state while authentication is being checked
  if (!user_id || !corporate_account || !account_description || !login_token) {
    return (
      <header className="fixed top-0 left-0 w-full bg-blue-900 shadow-md z-50">
        <div className="w-full mx-auto px-2 py-2 flex items-center justify-center">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span className="text-white text-sm">Checking authentication...</span>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="fixed top-0 left-0 w-full bg-blue-900 shadow-md z-50">
      {/* Changed from max-w-7xl to w-full with reduced horizontal padding */}
      <div className="w-full mx-auto px-2 py-2 flex items-center justify-between">

        {/* Logo and Navigation */}
        <div className="flex items-center space-x-4">
          {/* Logo Image */}
          <Link to="/RequirementsPortal" className="flex items-center">
            <img src="/logos/logo-reqsharp-yellow.svg" alt="Company Logo" className="h-12 w-auto mr-2" />
          </Link>

          <div className="relative nav-menu-container">
            <button
              onClick={() => setShowNavMenu(!showNavMenu)}
              className="text-white hover:text-blue-200"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {showNavMenu && (
              <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1">
                <button
                  onClick={handleBackToWebsite}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-100 hover:text-blue-800"
                >
                  Back to ReqSharp Website
                </button>
                <Link to="/UpdateUserAccount" className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-100 hover:text-blue-800">
                  My Account - {userName}
                </Link>
                <Link to="/feedback" className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-100 hover:text-blue-800">
                  Submit Feedback / Question
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-100 hover:text-blue-800"
                >
                  Logout
                </button>
              </div>
            )}
          </div>

          <Link to="/RequirementsPortal">
            <svg className="w-6 h-6 text-white hover:text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </Link>
        </div>

        {/* Main Navigation Menu - Added flex-1 to allow expansion */}
        <div className="flex items-center justify-center space-x-8 flex-1 mx-8">
          {menuSections.map((section, idx) => (
            <div key={idx} className="relative menu-container">
              <button
                onClick={() => handleMenuClick(idx)}
                className={`py-2 px-3 transition-colors duration-200 whitespace-nowrap ${
                  isProjectSelected
                    ? 'text-white hover:text-blue-200 cursor-pointer'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                disabled={!isProjectSelected}
                title={!isProjectSelected ? 'Please select a project to access this menu' : ''}
              >
                {section.title}
              </button>
              {openMenuId === idx && isProjectSelected && (
                <div className="absolute left-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1">
                  {section.items.map((item, itemIdx) => (
                    <Link
                      key={itemIdx}
                      to={item.path}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setOpenMenuId(null)}
                    >
                      {item.text}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right Side Icons */}
        <div className="flex items-center space-x-4">

          <div className="relative account-menu-container">
            <button
              onClick={() => setShowAccountMenu(!showAccountMenu)}
              className="text-white hover:text-blue-200"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A1.5 1.5 0 016.5 17h11a1.5 1.5 0 011.379.804l1.5 2.5A1.5 1.5 0 0119.5 22h-15a1.5 1.5 0 01-1.379-2.196l1.5-2.5zM12 2a5 5 0 00-5 5v1a5 5 0 0010 0V7a5 5 0 00-5-5z" />
              </svg>
            </button>

            {showAccountMenu && (
              <div className="absolute left-[-12rem] mt-2 w-48 bg-white rounded-md shadow-lg py-1">
                <Link to="/UpdateUserAccount" className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-100 hover:text-blue-800">
                  My Account - {userName}
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-100 hover:text-blue-800"
                >
                  Logout
                </button>
              </div>
            )}
          </div>

          {/* User Name Display */}
          {userName && (
            <span className="text-blue-900 font-medium ml-30">__________________</span>
          )}

        </div>
      </div>
    </header>
  );
};

export default Header;